import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "../auditUtils";
import JobsListPagination from "../JobsListPagination";
import { getListRangeLabel } from "../aiVisibilityListConstants";
import { isDualLensJobComplete } from "../../../services/dualLensApi";

function isJobReady(job) {
	return isDualLensJobComplete(job?.status);
}

export default function DualLensJobsCard({
	jobs,
	loading,
	total,
	page,
	pageSize,
	hasMore,
	onPageChange,
	onRegenerate,
	regeneratingJobId,
	onOpenCompare,
}) {
	const navigate = useNavigate();
	const list = jobs ?? [];

	return (
		<Card className="shadow-sm dual-lens__jobs-card mt-4">
			<CardHeader className="bg-white border-bottom">
				<h5 className="mb-0">Your Dual Lens View runs</h5>
				<p className="text-muted small mb-0 mt-1">
					{loading
						? "Loading…"
						: `${total || list.length} run${(total || list.length) !== 1 ? "s" : ""} total · ${getListRangeLabel(
								page,
								pageSize,
								total || list.length
						  )}`}
				</p>
			</CardHeader>
			<CardBody>
				{loading ? (
					<div className="text-center py-4">
						<Spinner size="sm" />
					</div>
				) : list.length === 0 ? (
					<div className="text-center py-4">
						<p className="text-muted mb-3">No Dual Lens View runs yet.</p>
						{onOpenCompare && (
							<Button color="primary" size="sm" onClick={onOpenCompare}>
								Fetch By URL & Compare
							</Button>
						)}
					</div>
				) : (
					<>
					<div className="d-flex flex-column gap-3">
						{list.map((job) => {
							const ready = isJobReady(job);
							const isRegenerating = regeneratingJobId === job.id;

							return (
								<div key={job.id} className="dual-lens__job-row border rounded p-3">
									<div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
										<div className="d-flex flex-wrap align-items-center gap-2">
											{job.status_label && (
												<span
													className={
														ready
															? "writing-assistance__ready-badge"
															: "writing-assistance__status-badge"
													}
												>
													{ready && <i className="fas fa-check me-1" />}
													{job.status_label}
												</span>
											)}
											{job.created_at && (
												<span className="text-muted small">
													{formatDistanceToNow(job.created_at)}
												</span>
											)}
										</div>
										<div className="d-flex align-items-center gap-2">
											<Button
												color="link"
												className="dual-lens__regenerate-btn p-0"
												disabled={isRegenerating}
												onClick={async () => {
													try {
														await onRegenerate(job.id);
													} catch {
														toast.error("Failed to regenerate job");
													}
												}}
												title="Regenerate"
											>
												<i
													className={`fas fa-sync-alt${
														isRegenerating ? " fa-spin" : ""
													}`}
												/>
											</Button>
											{ready && (
												<Button
													color="light"
													size="sm"
													className="dual-lens__view-btn border"
													onClick={() =>
														navigate(
															`/aivisibility/dual-lens/view?jobId=${encodeURIComponent(job.id)}`
														)
													}
												>
													<i className="far fa-eye me-1" />
													View
												</Button>
											)}
										</div>
									</div>
									{job.url && (
										<a
											href={job.url}
											target="_blank"
											rel="noopener noreferrer"
											className="dual-lens__job-url d-block text-break mb-0"
										>
											{job.url}
											<i className="fas fa-external-link-alt ms-1 small" />
										</a>
									)}
									{job.error_message && (
										<p className="text-danger small mb-0 mt-2">{job.error_message}</p>
									)}
								</div>
							);
						})}
					</div>
					<JobsListPagination
						page={page}
						pageSize={pageSize}
						total={total}
						hasMore={hasMore}
						onPageChange={onPageChange}
					/>
					</>
				)}

			</CardBody>
		</Card>
	);
}
